import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

(() => {
  const setStatus = (offer, message) => {
    let status = offer.querySelector('[data-ea-cart-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'ea-duo-offer__cart-status';
      status.dataset.eaCartStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      const recommendations = offer.querySelector('.ea-duo-offer__recommendations');
      if (recommendations) offer.insertBefore(status, recommendations);
      else offer.append(status);
    }
    status.textContent = message || '';
    status.hidden = !message;
  };

  const readProperties = (form) => {
    const first = {};
    const second = {};
    for (const control of Array.from(form.elements)) {
      if (!control || !control.name || !control.name.startsWith('properties[') || control.disabled) continue;
      const match = control.name.match(/^properties\[(.+)\]$/);
      if (!match) continue;
      const value = String(control.value || '').trim();
      if (!value) continue;
      const target = control.hasAttribute('data-ea-second-field') ? second : first;
      target[match[1]] = value;
    }
    return { first, second };
  };

  const normalizeVariantId = (value) => {
    const match = String(value || '').match(/(\d+)\D*$/);
    return match ? match[1] : '';
  };

  const getCart = async () => {
    const response = await fetch(Theme.routes.cart_url + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Could not refresh cart.');
    return response.json();
  };

  const addDuo = async (form, offer, event) => {
    const selectedVariant = new FormData(form).get('id');
    const firstVariantId = normalizeVariantId(selectedVariant);
    const secondChoice = offer.querySelector('[data-ea-second-variant]:checked');
    const secondVariantId = normalizeVariantId(secondChoice ? secondChoice.value : firstVariantId);
    if (!firstVariantId || !secondVariantId) {
      setStatus(offer, offer.dataset.eaDuoError);
      return;
    }

    const properties = readProperties(form);
    const items = [
      { id: Number(firstVariantId), quantity: 1, properties: properties.first },
      { id: Number(secondVariantId), quantity: 1, properties: properties.second }
    ];
    const cartSections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const payload = { items, sections: cartSections.join(',') };
    const productForm = form.closest('product-form-component');
    const lines = items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }));
    const deferred = CartLinesUpdateEvent.createPromise();
    if (productForm) {
      productForm.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise
      }));
    }

    const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
    const wasDisabled = submitter ? submitter.disabled : false;
    if (submitter) submitter.disabled = true;
    offer.dataset.eaAdding = 'true';
    setStatus(offer, '');

    let added = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        const detail = result.description || result.message || offer.dataset.eaDuoError;
        throw new Error(detail);
      }
      added = true;
      const cart = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: productForm ? productForm.id : '',
          itemCount: 2,
          productId: productForm ? productForm.dataset.productId : '',
          sections: result.sections,
          didError: false
        }
      });
      setStatus(offer, offer.dataset.eaDuoAdded);
    } catch (error) {
      const message = offer.dataset.eaDuoError || error.message;
      if (productForm) {
        productForm.dispatchEvent(new CartErrorEvent({
          error: error.message || message,
          code: 'INVALID',
          detail: { description: error.message || message }
        }));
      }
      setStatus(offer, added ? offer.dataset.eaDuoAdded : message);
      if (!added) {
        try {
          const cart = await getCart();
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'product-form-component',
              sourceId: productForm ? productForm.id : '',
              itemCount: 0,
              productId: productForm ? productForm.dataset.productId : '',
              didError: true
            }
          });
        } catch (refreshError) {
          deferred.reject(refreshError);
        }
      } else {
        deferred.reject(error);
      }
    } finally {
      offer.dataset.eaAdding = 'false';
      if (submitter) submitter.disabled = wasDisabled;
    }
  };

  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const offer = Array.from(document.querySelectorAll('eliseamour-duo-offer'))
      .find((candidate) => candidate.dataset.formId === form.getAttribute('id'));
    if (!offer) return;
    if (offer.dataset.eaAdding === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (offer.querySelector('[data-ea-pack]:checked')?.value !== '2') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addDuo(form, offer, event);
  }, true);

  const syncOffer = (offer) => {
    const isDuo = offer.querySelector('[data-ea-pack]:checked')?.value === '2';
    offer.querySelectorAll('[data-ea-second-wrap], [data-ea-duo-only]').forEach((wrap) => {
      wrap.hidden = !isDuo;
    });
    offer.querySelectorAll('[data-ea-second-field]').forEach((input) => {
      input.disabled = !isDuo;
      input.required = isDuo;
    });
    offer.querySelectorAll('[data-ea-second-variant]').forEach((select) => {
      select.disabled = !isDuo;
    });
  };

  const syncAll = () => {
    document.querySelectorAll('eliseamour-duo-offer').forEach(syncOffer);
  };

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches('[data-ea-pack]')) return;
    const offer = target.closest('eliseamour-duo-offer');
    if (offer) syncOffer(offer);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll, { once: true });
  } else {
    syncAll();
  }
})();
